/**
 * "Me do it" (docs/claims.md, docs/roadmap-2026-10.md `claimUI`): a peon marks an open task as
 * theirs for an hour, so two people do not both start the fridge. Pure rules shared by the server
 * (server/claims.ts refuses a live claim held by someone else), the web (src/views/TaskDetail.tsx,
 * the board card) and the app (client/screens/TaskDetail.tsx, client/lib/board.ts).
 *
 * A claim lives in the task row (claimed_by, claimed_at; migration 0036). It is live for CLAIM_MS
 * after it was made and then simply reads as nobody: nothing is written to clear it, nobody is told,
 * and nobody pays for letting it run out. Completing a task neither needs nor clears a claim.
 */

/** How long a claim holds: one hour. */
export const CLAIM_MS = 60 * 60 * 1000;

/** The task fields the rules read. */
export interface ClaimFields {
  status: string;
  claimed_by?: string | null;
  claimed_at?: string | null;
}

function sameName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** When the claim ends (ms), or null when there is none or it cannot be read. */
function claimEnds(task: ClaimFields): number | null {
  if (!task.claimed_by || !task.claimed_at) return null;
  const at = Date.parse(task.claimed_at);
  return Number.isNaN(at) ? null : at + CLAIM_MS;
}

/** Who holds a live claim on this open task, or null (no claim, an expired one, or not open). */
export function activeClaim(task: ClaimFields, now: number): string | null {
  if (task.status !== "open") return null;
  const ends = claimEnds(task);
  if (ends === null || now >= ends) return null;
  // A claim made a moment "in the future" (a clock a little ahead) is still a claim.
  return task.claimed_by ?? null;
}

/** Whole minutes left on a live claim, at least 1; null when there is no live claim. */
export function claimMinutesLeft(task: ClaimFields, now: number): number | null {
  if (activeClaim(task, now) === null) return null;
  const ends = claimEnds(task) as number;
  return Math.max(1, Math.min(CLAIM_MS / 60_000, Math.ceil((ends - now) / 60_000)));
}

/** Whether `me` holds the live claim. */
export function isMyClaim(task: ClaimFields, me: string, now: number): boolean {
  const who = activeClaim(task, now);
  return who !== null && sameName(who, me);
}

/**
 * The task screen's line under the meta: "You're on it · 42 min left", "Hue is on it · 12 min left";
 * null when nobody holds a live claim.
 */
export function claimLabel(task: ClaimFields, me: string, now: number): string | null {
  const who = activeClaim(task, now);
  if (who === null) return null;
  const left = claimMinutesLeft(task, now);
  return `${sameName(who, me) ? "You're" : `${who} is`} on it · ${left} min left`;
}

/**
 * The claim button on the task screen: "Me do it" when nobody holds it (or the claim ran out),
 * "Never mind" on your own live claim (it lets go), and none while someone else holds it: their
 * claim runs out by itself, nobody steals it.
 */
export function claimButton(task: ClaimFields, me: string, now: number): "claim" | "release" | null {
  if (task.status !== "open") return null;
  const who = activeClaim(task, now);
  if (who === null) return "claim";
  return sameName(who, me) ? "release" : null;
}

export const CLAIM_BUTTON_LABELS = { claim: "Me do it", release: "Never mind" } as const;

/** What the server answers when someone else holds a live claim. */
export function claimRefusal(holder: string): string {
  return `${holder} is on it.`;
}

/** The first name a board card shows beside the hand ("Hue" for "Hue Smith"). */
export function claimFirstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** The card's accessible words for a live claim: "Hue is on it". */
export function claimCardWords(name: string): string {
  return `${claimFirstName(name)} is on it`;
}

/**
 * The tiny pixel hand a claimed card shows beside the name: a 7x7 raised orc hand (three fingers
 * and a thumb) in the mascot's greens (scripts/make-mascot.mjs skinHi, skinLight, skinDeep). "." is
 * transparent. Both clients draw it as crisp rects, two CSS pixels per art pixel.
 */
export const CLAIM_HAND: readonly string[] = [
  ".h.h.h.",
  ".g.g.g.",
  ".ggggg.",
  "hgggggd",
  ".gggggd",
  "..gggd.",
  "..gdd..",
];
export const CLAIM_HAND_COLOURS: Readonly<Record<string, string>> = { h: "#7bc47f", g: "#5a9a4e", d: "#2a5227" };

/** The hand as rects (x, y, colour), one per lit pixel, for an SVG on either client. */
export function claimHandPixels(): { x: number; y: number; fill: string }[] {
  const out: { x: number; y: number; fill: string }[] = [];
  CLAIM_HAND.forEach((row, y) => [...row].forEach((ch, x) => {
    const fill = CLAIM_HAND_COLOURS[ch];
    if (fill) out.push({ x, y, fill });
  }));
  return out;
}

// ---------- Nudges ----------

/** The server's answer to a second nudge about the same task from the same peon on the same day. */
export const NUDGE_LIMIT_MESSAGE = "You nudged about this today already.";

/** The nudge push: title "Nudge: Run Dishwasher", body "Hue says please." */
export function nudgeTitle(taskTitle: string): string {
  return `Nudge: ${taskTitle}`;
}
export function nudgeBody(from: string): string {
  return `${from} says please.`;
}

/** Start of the device's local day (ISO UTC), the window the one-a-day rule counts in. */
export function startOfLocalDay(now: Date = new Date()): string {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/**
 * The server's window start: the device's `since` when it is within the last day (a device's
 * midnight can be up to 24 hours back), else 24 hours back, so a bad or missing value never opens
 * the gate wider than a day or shuts it for longer.
 */
export function nudgeWindowStart(since: string | null | undefined, now: number): string {
  const floor = now - 24 * 60 * 60 * 1000;
  const at = since ? Date.parse(since) : NaN;
  if (Number.isNaN(at) || at < floor || at > now) return new Date(floor).toISOString();
  return new Date(at).toISOString();
}
